<template>
  <ClientOnly v-if="!error">
    <Reader2
      v-show="showReader"
      class="max-h-screen !h-[100dvh]"
      :flag="startingFlag"
      :source-reading-direction="title?.attributes.readingMode ?? 'ltr'"
      :reader-manager="reader"
      :reader-settings="readerSettings"
      :error="readerError"
      :pending="readerPending"
    />
  </ClientOnly>
  <ErrorPage v-else :error="ApiToNuxtError(error)" />
</template>

<script setup lang="ts">
import { Chapter, isTitle, Statistics } from "~/src/api";
import { useAsyncData } from "#app";
import {
  extractFromLocale,
  migrateQualitySetting,
  useChapterGet,
} from "#imports";
import {
  initializeReaderManager,
  ReaderManager,
} from "~/src/reader/managers/reader";
import { useReaderSettings } from "~/stores/readerSettings";
import { ApiToNuxtError } from "~/utils/errors/404";
import naturalEval from "~/src/naturalEval";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import {
  getReaderTitle,
  getReaderTitleChapters,
} from "~/src/reader/managers/title";
import { getReaderChapter } from "~/src/reader/managers/chapter";
import { promptMature } from "~/src/reader/ux";
import { getChapterSequenceForLang } from "~/src/reader/generic";

/*
 * Logic flow:
 * 1. Fetch title & chapter (done server side and client side)
 * 2. Check mature status (done client side)
 * 3. Initialize reader (done client side)
 * 4. Enable chapter view tracker (done client side)
 */

/*
 * Image preload strategy
 * 1. Load first page and block reader load
 * 2. after reader load, if hint animation load 2nd page?
 * 3. after hint animation, load rest sequentially
 */

const nuxtApp = useNuxtApp();
const { t: translate, locale } = nuxtApp.$i18n.global;
const authStore = nuxtApp.$auth();
const appStore = nuxtApp.$app();
const settingsStore = nuxtApp.$settings();
const config = useRuntimeConfig();
const route = useRoute();
const { selectLocalizedCover } = useMediaLink();

const chapterId = computed<string>(() => route.params.id as string);

async function tryGetToken() {
  await authStore?.waitUntilRefreshIsComplete();
  return (await authStore?.getToken()) || null;
}

const queryClient = useQueryClient();
const reader = reactive(new ReaderManager(() => queryClient, tryGetToken));

const {
  data: chapter,
  suspense: chapterPrefetch,
  error,
} = getReaderChapter(() => queryClient, chapterId);

const fetchedTitleId = computed(
  () => chapter.value?.relationships.find(isTitle)?.id ?? "",
);

const {
  data: title,
  suspense: titlePrefetch,
  error: titleError,
} = getReaderTitle(() => queryClient, fetchedTitleId);

const { data: aggregate, suspense: aggregatePrefetch } = getReaderTitleChapters(
  () => queryClient,
  fetchedTitleId,
);

const { data: chapterLocks, isPending: locksPending } = useQuery({
  ...useChapterAccess(
    computed(() => reader.chapterIdSequence),
    "chapter",
  ),
  enabled: computed(() => !!reader.chapterIdSequence.length),
});

watch(chapterLocks, (v) => {
  if (v) {
    Object.entries(v).forEach(([key, value]) => {
      reader.accessMap.set(key, { gated: !value, access: true });
    });
  }
});

watch(
  aggregate,
  (agg) => {
    if (!agg) return;
    const sequence = getChapterSequenceForLang(
      "chapters",
      agg,
      chapter.value?.attributes.translatedLanguage ?? "en",
    );
    reader.updateChapterIdSequence(sequence.map((c) => c.id));
  },
  { immediate: true },
);

const readerSettings = useReaderSettings();
const showReader = ref(true);

const mappedLocale = computed(() => reverseLocaleMap(locale.value));
const currentChapterId = computed(() => reader.getCurrChapterId());

const { markerMutation, invalidate } = useChapterReadMarkerManager(
  queryClient,
  fetchedTitleId,
);
const { mutate: updateReadMarkers } = markerMutation;

const previousId = ref<string | null>(null);
watch(currentChapterId, (id) => {
  // wait for a manual first call of the view function before beginning automated watch
  if (!id || !previousId.value) return;
  performChapterViewActions(id);
});

const performChapterViewActions = (id: string) => {
  if (previousId.value === id) return;
  previousId.value = id;
  console.debug(
    "%cReader%c Performing chapter view actions for %c%s",
    "color:black;background-color:#ff99ff;padding:2px 4px;border-radius:4px;",
    "",
    "font-weight:bold",
    id,
  );

  navigateTo(linkTo("chapter", id), { replace: true });

  authStore?.getToken().then((token) => {
    if (token)
      return Chapter.pushHistory(id, token, Math.floor(Date.now() / 1000));
  });
  updateReadMarkers({ add: [id], noRefresh: true });
};

onServerPrefetch(async () => {
  await chapterPrefetch();
  await titlePrefetch();
  await aggregatePrefetch();

  nuxtApp.runWithContext(() => {
    if (error.value) use404Response(ApiToNuxtError(error.value));
    if (!chapter.value || !title.value) return;

    const chapterName = createChapterName(
      chapter.value.attributes.chapter,
      chapter.value.attributes.volume,
      null,
      "short",
      title.value.attributes.chapterKind || undefined,
      title.value.attributes.volumeKind || undefined,
    );

    const titleTitle = extractFromLocale(
      locale.value,
      title.value.attributes.title,
    );

    const titleDescription = extractFromLocale(
      locale.value,
      title.value.attributes.description,
    );

    const titleCover = selectLocalizedCover(locale.value, title.value, "512");

    useServerSeoMeta({
      title: `${chapterName} - ${titleTitle}`,
      ogTitle: `${chapterName} - ${titleTitle}`,
      twitterTitle: titleTitle,
      description: titleDescription,
      ogDescription: titleDescription,
      twitterDescription: titleDescription,
      ogUrl: config.public.baseUrl + linkTo(title.value),
      ogLocale: locale.value,
      ogImage: titleCover,
      ogImageSecureUrl: titleCover,
      twitterImage: titleCover,
      ogImageHeight: "1200",
      ogImageWidth: "630",
      ogImageUrl: titleCover,
      ogImageType: "image/png",
      ogImageAlt: "NamiComi",
      ogLocaleAlternate: "en_GB",
    });
  });
});

const seo = computed(() =>
  title.value && chapter.value
    ? {
        chapter: `${
          chapter.value.attributes.volume
            ? `Vol. ${chapter.value.attributes.volume} `
            : ""
        }${
          chapter.value.attributes.chapter
            ? `Ch. ${chapter.value.attributes.chapter}`
            : "Oneshot"
        }`,
        title: extractFromLocale(locale.value, title.value.attributes.title),
        description: extractFromLocale(
          locale.value,
          title.value.attributes.description,
        ),
      }
    : {},
);

useHead({
  title: () => `${seo.value.chapter ?? ""} - ${seo.value.title ?? ""}`,
  link: () => [
    {
      rel: "canonical",
      href: `${config.public.baseUrl}/en/chapter/${route.params.id}`,
    },
  ],
  meta: () => [
    {
      property: "og:title",
      content: `${seo.value.chapter ?? ""} - ${seo.value.title ?? ""} - ${
        config.public.siteName
      }`,
    },
    { name: "description", content: seo.value.description },
  ],
});

const startingFlag = ref<{
  new: boolean;
  chapterId: string;
  sourceUrl: string;
  highUrl: string;
  mediumUrl: string;
  lowUrl: string;
}>();

// logic to start from an arbitrary chapter from a title
const {
  pending: readerPending,
  error: readerError,
  refresh: initializeReader,
} = useAsyncData(
  async () => {
    await readerSettings.initialize();
    await authStore?.waitUntilRefreshIsComplete();

    if (!chapterId.value) return;

    startingFlag.value = await initializeReaderManager(
      () => queryClient,
      reader,
      chapterId.value,
      {
        quality: migrateQualitySetting(settingsStore?.settings.site.quality),
        pagesPerGroup: readerSettings.readingMode === "double" ? 2 : 1,
        startingPage: route.query.sp
          ? {
              hash: route.query.sp as string,
              flag: !!route.query.h,
            }
          : undefined,
        chapterIdSequence: aggregate.value
          ? getChapterSequenceForLang(
              "chapters",
              aggregate.value,
              chapter.value?.attributes.translatedLanguage ?? "en",
            ).map((c) => c.id)
          : undefined,
      },
    );

    if (!title.value) return;

    const ctx = {
      "title.readingMode": title.value.attributes.readingMode,
      "screen.width": window.innerWidth,
    };
    const profiles = readerSettings.getAvailableProfiles(ctx);
    const selectedProfile = profiles.find(
      (profile) => profile.condition && naturalEval(profile.condition, ctx),
    );
    readerSettings.applyProfile(
      selectedProfile?.profile || profiles[0].profile,
      ctx,
    );

    if (readerSettings.hideReadingHint)
      reader.loadChapterPages(chapterId.value);

    return;
  },
  { server: false, immediate: false },
);

const { data: currentChapter } = useQuery({
  ...useChapterGet(currentChapterId, ["organization", "user"], tryGetToken),
  enabled: computed(() => !readerPending.value),
});

function goToLogin() {
  authStore?.login({
    redirectBackTo: route.fullPath,
    locale: mappedLocale.value,
  });
}

onMounted(async () => {
  invalidate();
  if (!authStore) return;
  useHead({
    title: () => {
      if (!chapterId.value || !currentChapter.value)
        return `${seo.value?.chapter ?? ""} - ${seo.value?.title ?? ""}`;

      return `${createChapterName(
        currentChapter.value.attributes.chapter,
        currentChapter.value.attributes.volume,
        null,
        "short",
        title.value?.attributes.chapterKind || undefined,
        title.value?.attributes.volumeKind || undefined,
      ).replace(",", "")} - ${extractFromLocale(
        locale.value,
        title.value?.attributes.title ?? {},
      )}`;
    },
  });

  let checkingMature = false;
  const unwatch = watch([chapter, title], checkMature, { immediate: true });

  async function checkMature() {
    if (!chapter.value || !title.value || !settingsStore || checkingMature)
      return;
    checkingMature = true;

    await settingsStore.waitForInitialization();

    if (
      !settingsStore.settings.platform.matureContentEnabled &&
      title.value.attributes.contentRating !== "safe"
    ) {
      showReader.value = false;
      const result = await promptMature();
      if (result === "cancel") return navigateTo(linkTo(title.value));

      if (authStore?.user)
        return navigateTo({
          path: `/${mappedLocale}/settings/account-preferences`,
          query: {
            returnTo: encodeURIComponent(route.fullPath),
          },
        });
      return goToLogin();
    } else if (chapter.value.attributes.workInProgress) {
      await appStore
        ?.prompt("This chapter is a work in progress!", {
          detail:
            "The creator has decided to make this chapter available to view before its completion. Anything you see may change at anytime.",
          icon: "love",
          buttons: {
            ok: {
              buttonType: "secondary",
              buttonText: "I Understand",
            },
          },
        })
        .catch(() => {});
    }

    // remove the watcher as we no longer need it
    unwatch();

    initializeReader().then(() => {
      // enable current chapter id watcher here since our external checks are finished and reader is loaded
      performChapterViewActions(chapterId.value!);
    });
  }
});

onUnmounted(() => {
  reader.destroy();
});
</script>
